import type { QuoteData } from "@/pages/broker/quoting/types";

// What gets persisted to quotes.wizardState so a draft can be resumed.
//
// This is an explicit field list, NOT a spread of quoteData. That is on purpose
// — quoteData carries things that must not be stored, like the whole selected
// client object — but it has a sharp edge: a field added to QuoteData and
// written by the wizard is silently dropped here unless it is named below. No
// error, no warning, it simply does not come back on resume. That has already
// cost one bug (an RFP's chosen general agents).
//
// There is a SECOND list to keep in step: quoteWizardStateSchema in
// shared/schemas/financial.ts. Zod drops unknown keys, so a field missing there
// never reaches the database however carefully it is sent from here. Adding a
// wizard field means touching both, and wizardState.test.ts fails if this half
// falls behind QuoteData.

type PremiumSummarySnapshot = {
  estimatedMonthlyPremium?: number | null;
  estimatedAnnualPremium?: number | null;
  estimatedEmployeePortion?: number | null;
  estimatedEmployerPortion?: number | null;
  planBreakdowns?: unknown[];
  byCoverageType?: Record<string, unknown>;
  premiumRangeMin?: number | null;
  premiumRangeMax?: number | null;
};

export type WizardStateMeta = {
  mode: "quote" | "rfp";
  highestCompletedStep: number;
  premiumSummary: PremiumSummarySnapshot;
  // Same shape, computed for currentPlans — without this snapshot the
  // public share-link page (which only ever reads what was saved, it has
  // no live premium-calculation hook of its own) has no premium to show
  // for the current plan at all, even though currentPlans itself is saved.
  currentPremiumSummary?: PremiumSummarySnapshot;
};

export function buildWizardState(quoteData: QuoteData, meta: WizardStateMeta) {
  const { premiumSummary, currentPremiumSummary } = meta;
  return {
    mode: meta.mode,
    clientType: quoteData.clientType,
    // The entity is stored by id — the full record belongs to its own table,
    // and a stale copy in here would quietly go out of date.
    selectedEntityId: quoteData.selectedEntity?.id,
    effectiveDate: quoteData.effectiveDate,
    coverageTypes: quoteData.coverageTypes,
    selectedEmployees: quoteData.selectedEmployees,
    selectedDependents: quoteData.selectedDependents,
    selectedCarriers: quoteData.selectedCarriers,
    rfpSendVia: quoteData.rfpSendVia,
    rfpGeneralAgencyIds: quoteData.rfpGeneralAgencyIds,
    rfpGeneralAgencyAsks: quoteData.rfpGeneralAgencyAsks,
    rfpRespondBy: quoteData.rfpRespondBy,
    rfpEmailGreeting: quoteData.rfpEmailGreeting,
    rfpEmailIntro: quoteData.rfpEmailIntro,
    rfpEmailClosing: quoteData.rfpEmailClosing,
    rfpEmailSignature: quoteData.rfpEmailSignature,
    selectedPlans: quoteData.selectedPlans, // Full plan objects, for public quote display
    currentPlans: quoteData.currentPlans,
    compareCurrentEnabled: quoteData.compareCurrentEnabled,
    rfpParameters: quoteData.rfpParameters,
    highestCompletedStep: meta.highestCompletedStep,
    confirmedRatingArea: quoteData.confirmedRatingArea || null,
    proposalSettings: quoteData.proposalSettings || undefined,
    contribution: quoteData.contribution || undefined,
    // Same field set the share step writes so a draft-only share renders the
    // same totals and ranges as a shared one.
    premiumSummary: {
      totalMonthlyPremium: premiumSummary.estimatedMonthlyPremium || 0,
      totalAnnualPremium: premiumSummary.estimatedAnnualPremium || 0,
      totalEmployeePortion: premiumSummary.estimatedEmployeePortion || 0,
      totalEmployerPortion: premiumSummary.estimatedEmployerPortion || 0,
      planBreakdowns: premiumSummary.planBreakdowns || [],
      byCoverageType: premiumSummary.byCoverageType || {},
      premiumRangeMin: premiumSummary.premiumRangeMin || 0,
      premiumRangeMax: premiumSummary.premiumRangeMax || 0,
    },
    // Same snapshot, for the current plan — what makes "Your Current Plan"
    // possible on the public share-link page.
    currentPremiumSummary: {
      totalMonthlyPremium: currentPremiumSummary?.estimatedMonthlyPremium || 0,
      totalAnnualPremium: currentPremiumSummary?.estimatedAnnualPremium || 0,
      totalEmployeePortion: currentPremiumSummary?.estimatedEmployeePortion || 0,
      totalEmployerPortion: currentPremiumSummary?.estimatedEmployerPortion || 0,
      planBreakdowns: currentPremiumSummary?.planBreakdowns || [],
      byCoverageType: currentPremiumSummary?.byCoverageType || {},
      premiumRangeMin: currentPremiumSummary?.premiumRangeMin || 0,
      premiumRangeMax: currentPremiumSummary?.premiumRangeMax || 0,
    },
  };
}

/**
 * QuoteData fields that deliberately never reach wizardState, with the reason.
 * wizardState.test.ts treats anything NOT listed here as a field that must be
 * persisted, so a new field fails the test until it is either saved or an
 * explicit decision is recorded here.
 */
export const INTENTIONALLY_NOT_PERSISTED: Record<string, string> = {
  selectedEntity: "stored as selectedEntityId; the full record lives in its own table",
};
